import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import { BRAND_PRIMARY_MAIN } from '../../theme/managementTheme';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import { useColorMode } from '../../context/colorModeContext';
import useUser from '../../hooks/useUser';

const PUBLIC_PATHS = new Set(['/', '/login', '/rfid-login', '/request-password-reset', '/reset-password']);

const OPTIONS = [
  {
    value: 'light',
    label: 'Hell',
    description: 'Hoher Kontrast bei Tageslicht',
    Icon: LightModeIcon,
  },
  {
    value: 'dark',
    label: 'Dunkel',
    description: 'Schonend für die Augen bei wenig Licht',
    Icon: DarkModeIcon,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Wie in den Geräteeinstellungen',
    Icon: BrightnessAutoIcon,
  },
];

export default function ThemeIntroductionModal() {
  const theme = useTheme();
  const location = useLocation();
  const auth = useAuth();
  const { colorModePreference, setIntroductionThemePreview } = useColorMode();
  const { updateUserPreferences } = useUser();
  const [selected, setSelected] = useState('dark');
  const [saving, setSaving] = useState(false);
  const userPickedRef = useRef(false);

  const shouldShow = useMemo(
    () =>
      Boolean(
        auth.token &&
          auth.userPreferencesHydrated &&
          !auth.isCounterDevice &&
          !PUBLIC_PATHS.has(location.pathname) &&
          Array.isArray(auth.hasBeenIntroducedTo) &&
          !auth.hasBeenIntroducedTo.includes('themeColorMode')
      ),
    [
      auth.token,
      auth.userPreferencesHydrated,
      auth.isCounterDevice,
      auth.hasBeenIntroducedTo,
      location.pathname,
    ]
  );

  useEffect(() => {
    if (!shouldShow) {
      userPickedRef.current = false;
      setIntroductionThemePreview(null);
      return;
    }
    if (!userPickedRef.current) {
      const initial = colorModePreference || 'dark';
      setSelected(initial);
      setIntroductionThemePreview(initial);
    }
  }, [shouldShow, colorModePreference, setIntroductionThemePreview]);

  const handlePick = value => {
    userPickedRef.current = true;
    setSelected(value);
    setIntroductionThemePreview(value);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const updated = await updateUserPreferences(
        { colorMode: selected },
        { acknowledgeIntroductions: ['themeColorMode'] }
      );
      if (updated?.preferences && auth.setPreferences) {
        auth.setPreferences(prev => ({ ...prev, ...updated.preferences }));
      }
      if (auth.setHasBeenIntroducedTo) {
        auth.setHasBeenIntroducedTo(prev => {
          const fromServer = Array.isArray(updated?.hasBeenIntroducedTo) ? updated.hasBeenIntroducedTo : [];
          return [...new Set([...(prev || []), ...fromServer, 'themeColorMode'])];
        });
      }
      setIntroductionThemePreview(null);
    } catch (_e) {
      toast.error('Speichern der Einstellungen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={shouldShow}
      onClose={() => {}}
      fullWidth
      maxWidth='sm'
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            // Fixed scrim so switching preview to light theme does not wash out the whole screen (MUI defaults lighten with palette.mode).
            backgroundColor: alpha('#000000', 0.52),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          backgroundImage: t =>
            t.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${theme.palette.background.paper} 42%)`
              : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.45)} 0%, ${theme.palette.background.paper} 40%)`,
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant='h5' component='h1' sx={{ fontWeight: 700 }}>
          Wie soll deine Oberfläche aussehen?
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1, maxWidth: 480 }}>
          Du kannst das Farbschema jederzeit in der Kopfzeile oder unter Einstellungen ändern.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 0.5 }}>
          {OPTIONS.map(({ value, label, description, Icon }) => {
            const isActive = selected === value;
            const hellSunYellow =
              value === 'light' && theme.palette.mode === 'light' && isActive;
            return (
              <Paper
                key={value}
                elevation={0}
                onClick={() => handlePick(value)}
                role='button'
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePick(value);
                  }
                }}
                sx={{
                  flex: 1,
                  p: 2,
                  cursor: 'pointer',
                  borderRadius: 2,
                  position: 'relative',
                  border: '2px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: theme =>
                    isActive
                      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08)
                      : alpha(theme.palette.action.hover, theme.palette.action.hoverOpacity),
                  transition: theme =>
                    theme.transitions.create(['border-color', 'box-shadow', 'background-color'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                  boxShadow: isActive ? 2 : 0,
                  '&:hover': {
                    borderColor: 'primary.light',
                  },
                }}
              >
                {isActive && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontSize: 22,
                      color: 'primary.main',
                    }}
                  />
                )}
                <Icon
                  sx={{
                    fontSize: 36,
                    mb: 1,
                    opacity: isActive ? 1 : 0.85,
                    ...(hellSunYellow ? { color: BRAND_PRIMARY_MAIN } : {}),
                  }}
                  color={hellSunYellow ? 'inherit' : isActive ? 'primary' : 'action'}
                />
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                  {label}
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                  {description}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'flex-end' }}>
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={handleConfirm}
          disabled={saving}
          sx={{ minWidth: 160, px: 3, fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? 'Speichern…' : 'Auswahl übernehmen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
